import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, ArrowRight, Check, Truck, Shield, Award, 
  ChevronRight, MessageCircle, X, Send, Sparkles,
  Home, Shirt, Smartphone, Heart, Watch, ShoppingBag,
  Mic, MicOff, Camera, Loader2, HeartPulse
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/ProductCard';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category icons mapping
const categoryIcons = {
  casa: Home,
  moda: Shirt,
  mobili: Home,
  elettronica: Smartphone,
  bellezza: Sparkles,
  gioielli: Watch,
  salute: HeartPulse,
  default: ShoppingBag
};

const HomePage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Voice search state
  const [isListening, setIsListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  
  // Image search state
  const [imageSearching, setImageSearching] = useState(false);
  const fileInputRef = useRef(null);
  
  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { from: 'bot', text: 'Buongiorno! Come posso assisterla oggi?' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef(null);
  
  // Scroll refs
  const trendingRef = useRef(null);

  useEffect(() => {
    fetchData();
    // Check voice support
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      setVoiceSupported(true);
    }
    // Handle resize
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchData = async () => {
    try {
      // Fetch featured products from different categories for variety
      const [landRoverRes, mobiliFeatured, modaFeatured, gioielliFeatured, categoriesRes, trendingRes] = await Promise.all([
        axios.get(`${API}/products?search=Land%20Rover&limit=1&lang=it`),
        axios.get(`${API}/products?category=mobili&featured=true&limit=1&lang=it`),
        axios.get(`${API}/products?category=moda&limit=1&lang=it`),
        axios.get(`${API}/products?category=gioielli&limit=1&lang=it`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/products?sort=popular&limit=12&lang=it`)
      ]);
      
      // Combine varied featured products
      const variedFeatured = [
        ...(landRoverRes.data.products || []),
        ...(mobiliFeatured.data.products || []),
        ...(modaFeatured.data.products || []),
        ...(gioielliFeatured.data.products || [])
      ].slice(0, 4);
      
      setFeaturedProducts(variedFeatured);
      setTrendingProducts(trendingRes.data.products);
      
      // Transform categories
      const catsObj = categoriesRes.data.categories;
      const catsArray = Object.entries(catsObj).map(([key, value]) => ({
        key,
        name: value.name,
        icon: categoryIcons[key] || categoryIcons.default
      }));
      setCategories(catsArray.slice(0, 6));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Voice search
  const startVoiceSearch = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    
    const recognition = new SpeechRecognition();
    recognition.lang = 'it-IT';
    recognition.interimResults = false;
    
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
      navigate(`/products?search=${encodeURIComponent(transcript)}`);
    };
    
    recognition.start();
  };

  // Image search
  const handleImageSearch = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImageSearching(true);
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post(`${API}/search/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.search_query) {
        navigate(`/products?search=${encodeURIComponent(response.data.search_query)}`);
      }
    } catch (error) {
      console.error('Image search error:', error);
      // Fallback: navigate to products
      navigate('/products');
    } finally {
      setImageSearching(false);
    }
  };

  const handleChatSend = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput.trim();
    setChatMessages(prev => [...prev, { from: 'user', text: userMessage }]);
    setChatInput('');
    
    try {
      const response = await axios.post(`${API}/chat?message=${encodeURIComponent(userMessage)}`);
      setChatMessages(prev => [...prev, { from: 'bot', text: response.data.response }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { from: 'bot', text: 'Mi scusi, si è verificato un errore. La prego di riprovare.' }]);
    }
  };

  const scrollTrending = (direction) => {
    if (trendingRef.current) {
      const scrollAmount = 320;
      trendingRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Category images - immagini appropriate per ogni categoria
  const categoryImages = {
    moda: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=600',
    casa: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600',
    mobili: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600',
    elettronica: 'https://images.unsplash.com/photo-1550009158-9ebf69173e03?w=600',
    bellezza: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=600',
    gioielli: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=600',
    fitness: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600',
    outdoor: 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=600',
    sport: 'https://images.unsplash.com/photo-1602991174407-a015b35a7b00?w=600',
    bambini: 'https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=600',
    pet: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=600',
    auto: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=600',
    giardino: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600',
    cucina: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600',
    ufficio: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600',
    toys: 'https://images.unsplash.com/photo-1558060370-d644479cb6f7?w=600',
    office: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600',
    diy: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=600'
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="home-page">
      {/* Hero Section - PIÙ IMPATTANTE */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background - QANTRI Logo HD */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#e8f0e8] via-[#d4e4d4] to-[#c0d8c0]">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[500px] md:max-w-[700px] opacity-80">
            <img
              src="https://customer-assets.emergentagent.com/job_7e0a43f4-efc2-4b14-a9d5-df49c56c0bab/artifacts/bm42y12d_IMG_8269.jpeg"
              alt="QANTRI"
              className="w-full h-auto"
              style={{ imageRendering: 'high-quality' }}
            />
          </div>
        </div>
        
        {/* Hero Content - FRASI PIÙ IMPATTANTI */}
        <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onClick={() => navigate('/products?collection=new')}
            className="inline-flex items-center gap-2 bg-[#1a5c3a]/80 backdrop-blur-sm px-4 py-2 rounded-full mb-8 cursor-pointer hover:bg-[#1a5c3a] transition-all"
          >
            <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse"></span>
            <span className="text-white text-xs uppercase tracking-[0.3em]">
              {t('newCollection')} →
            </span>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-4xl md:text-6xl lg:text-7xl font-light text-[#1a3d2a] leading-[1.1] mb-8 tracking-tight drop-shadow-sm"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {t('heroTagline')}
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-lg md:text-xl text-[#2d5a3d] font-light mb-6 max-w-2xl mx-auto leading-relaxed"
          >
            {t('heroDescription')}
          </motion.p>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-base md:text-lg text-[#4a7a5a] font-light mb-20 max-w-xl mx-auto"
          >
            {t('heroSubtitle')}
          </motion.p>
          
          {/* PROMINENT SEARCH BAR */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            onSubmit={handleSearch}
            className="relative max-w-2xl mx-auto"
          >
            <div className={`relative transition-all duration-500 ${searchFocused ? 'scale-105' : ''}`}>
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 h-6 w-6 text-neutral-400 z-10" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder={isMobile ? t('search') : (t('searchFull') || t('search'))}
                className="w-full bg-white/95 backdrop-blur-xl border-0 text-base md:text-lg py-5 md:py-6 pl-12 md:pl-16 pr-36 md:pr-56 shadow-2xl rounded-full placeholder:text-neutral-400 focus:outline-none focus:ring-4 focus:ring-emerald-900/20 transition-all"
                data-testid="hero-search-input"
              />
              
              {/* Voice & Image Search Buttons */}
              <div className="absolute right-24 md:right-32 top-1/2 -translate-y-1/2 flex items-center gap-1 md:gap-2">
                {/* Voice Search */}
                {voiceSupported && (
                  <button
                    type="button"
                    onClick={startVoiceSearch}
                    className={`p-3 rounded-full transition-all ${
                      isListening 
                        ? 'bg-red-500 text-white animate-pulse' 
                        : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
                    }`}
                    title="Ricerca vocale"
                    data-testid="voice-search-btn"
                  >
                    {isListening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                  </button>
                )}
                
                {/* Image Search */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={imageSearching}
                  className="p-3 rounded-full bg-neutral-100 text-neutral-500 hover:bg-neutral-200 transition-all"
                  title="Cerca con immagine"
                  data-testid="image-search-btn"
                >
                  {imageSearching ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Camera className="h-5 w-5" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSearch}
                  className="hidden"
                />
              </div>
              
              <button 
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#064E3B] text-white px-8 py-4 rounded-full text-sm uppercase tracking-wider font-medium hover:bg-[#043927] transition-all duration-300"
                data-testid="hero-search-btn"
              >
                Cerca
              </button>
            </div>
            
            {/* Quick Links - TUTTE LE CATEGORIE */}
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {[
                { name: 'Premium', key: 'lusso', special: true },
                { name: 'Sale', key: 'offerte', special: true },
                { name: 'Moda', key: 'moda' },
                { name: 'Casa', key: 'casa' },
                { name: 'Mobili', key: 'mobili' },
                { name: 'Tech', key: 'elettronica' },
                { name: 'Beauty', key: 'bellezza' },
                { name: 'Fitness', key: 'fitness' },
                { name: 'Outdoor', key: 'outdoor' },
                { name: 'Sport', key: 'sport' },
                { name: 'Gioielli', key: 'gioielli' },
                { name: 'Kids', key: 'bambini' },
                { name: 'Pet', key: 'pet' },
                { name: 'Auto', key: 'auto' },
                { name: 'Toys', key: 'toys' },
                { name: 'Office', key: 'office' },
                { name: 'DIY', key: 'diy' }
              ].map((cat) => (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => navigate(`/products?category=${cat.key}`)}
                  className={`px-5 py-2.5 text-sm rounded-full transition-all shadow-md hover:shadow-lg ${
                    cat.special 
                      ? 'bg-gradient-to-r from-[#D4AF37] to-[#B8962D] text-black font-semibold' 
                      : 'bg-white text-[#1a5c3a] font-medium border-2 border-[#1a5c3a]/20 hover:border-[#1a5c3a]/40 hover:bg-[#1a5c3a]/5'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </motion.form>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center text-white/50"
        >
          <span className="text-xs uppercase tracking-[0.3em] mb-3">Scorri</span>
          <motion.div 
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-px h-12 bg-gradient-to-b from-white/50 to-transparent" 
          />
        </motion.div>
      </section>

      {/* NEW ARRIVALS Section */}
      <section className="py-16 md:py-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4 block">
                ✨ Appena Arrivati
              </span>
              <h2 className="text-3xl md:text-5xl font-light tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Nuova <span className="italic">Collezione</span>
              </h2>
            </div>
            <Link to="/products?sort=newest" className="mt-6 md:mt-0 group">
              <span className="text-sm text-white/70 hover:text-[#D4AF37] transition-colors flex items-center gap-2">
                Vedi tutti i nuovi arrivi
                <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {featuredProducts.slice(0, 4).map((product, i) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Link 
                  to={`/products/${product.id}`}
                  className="group block"
                >
                  <div className="aspect-square bg-white/5 rounded-lg overflow-hidden mb-3">
                    <img
                      src={product.images?.[0] || 'https://via.placeholder.com/300'}
                      alt={product.title?.it || product.title?.en || 'Prodotto'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-wider text-[#D4AF37]">Nuovo</span>
                  </div>
                  <h3 className="text-sm font-medium text-white/90 line-clamp-1 group-hover:text-[#D4AF37] transition-colors">
                    {product.title?.it || product.title?.en || product.title || 'Prodotto'}
                  </h3>
                  <p className="text-lg font-bold text-white mt-1">€{(product.selling_price || product.price || 0).toFixed(2)}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Bento Grid */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end md:justify-between mb-12"
          >
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-4 block">
                Esplora per Categoria
              </span>
              <h2 className="text-3xl md:text-5xl font-light tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Le nostre <span className="italic">collezioni</span>
              </h2>
            </div>
            <Link to="/products" className="mt-6 md:mt-0 group">
              <span className="text-sm text-neutral-500 hover:text-[#064E3B] transition-colors flex items-center gap-2">
                Vedi tutto
                <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </motion.div>
          
          {/* Bento Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.map((cat, i) => {
              const isLarge = i === 0 || i === 3;
              const Icon = cat.icon;
              return (
                <motion.div
                  key={cat.key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`${isLarge ? 'md:col-span-2 md:row-span-2' : ''}`}
                >
                  <Link 
                    to={`/products?category=${cat.key}`}
                    className="group relative block overflow-hidden bg-neutral-100 aspect-square md:aspect-auto md:h-full min-h-[200px]"
                    data-testid={`category-${cat.key}`}
                  >
                    <img
                      src={categoryImages[cat.key] || categoryImages.moda}
                      alt={cat.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                      </div>
                      <h3 className="text-white text-xl md:text-2xl font-light">{cat.name}</h3>
                      <span className="text-white/60 text-sm flex items-center gap-1 mt-2 group-hover:gap-2 transition-all">
                        Esplora <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trending Products - Horizontal Scroll */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-end justify-between mb-12"
          >
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4 block flex items-center gap-2">
                <Sparkles className="h-4 w-4" /> Trending Now
              </span>
              <h2 className="text-3xl md:text-4xl font-light tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                I più <span className="italic">desiderati</span>
              </h2>
            </div>
            <div className="hidden md:flex gap-2">
              <button 
                onClick={() => scrollTrending('left')}
                className="w-12 h-12 rounded-full border border-neutral-200 flex items-center justify-center hover:bg-neutral-100 transition-colors"
              >
                <ChevronRight className="h-5 w-5 rotate-180" />
              </button>
              <button 
                onClick={() => scrollTrending('right')}
                className="w-12 h-12 rounded-full border border-neutral-200 flex items-center justify-center hover:bg-neutral-100 transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </motion.div>
        </div>
        
        {/* Horizontal Scroll Container */}
        <div 
          ref={trendingRef}
          className="flex gap-6 overflow-x-auto pb-6 px-6 md:px-12 lg:px-24 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="flex-shrink-0 w-72 aspect-[3/4] bg-neutral-100 animate-pulse snap-start" />
            ))
          ) : (
            trendingProducts.map((product, i) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="flex-shrink-0 w-72 snap-start"
              >
                <ProductCard product={product} variant="luxury" />
              </motion.div>
            ))
          )}
        </div>
      </section>

      {/* Featured Selection */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end md:justify-between mb-12"
          >
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-4 block">
                Selezione QANTRI
              </span>
              <h2 className="text-3xl md:text-4xl font-light tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Scelti per <span className="italic">te</span>
              </h2>
            </div>
            <Link to="/products" className="mt-6 md:mt-0 group">
              <span className="text-sm text-neutral-500 hover:text-[#064E3B] transition-colors flex items-center gap-2">
                Esplora la collezione
                <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {loading ? (
              [...Array(8)].map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-neutral-100 animate-pulse" />
              ))
            ) : (
              featuredProducts.map((product, i) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                >
                  <ProductCard product={product} variant="luxury" />
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-20 bg-[#064E3B] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">
            {[
              { icon: Award, title: 'Qualità Premium', desc: 'Solo il meglio, selezionato per te' },
              { icon: Check, title: 'Autenticità Garantita', desc: 'Prodotti verificati e certificati' },
              { icon: Shield, title: 'Pagamenti Sicuri', desc: 'Transazioni protette al 100%' },
              { icon: Truck, title: 'Spedizione Veloce', desc: 'Consegna tracciata e assicurata' },
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center">
                  <item.icon className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <h3 className="text-sm uppercase tracking-[0.15em] mb-3 font-medium">{item.title}</h3>
                <p className="text-sm text-white/60 font-light">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Logistica Section - NEW */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-[#f8faf8] to-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-xs uppercase tracking-[0.3em] text-[#1a5c3a] mb-4 block">
              Affidabilità Garantita
            </span>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Magazzino e <span className="italic">Spedizioni</span>
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto text-lg font-light">
              Magazzino fisico in Italia e fornitori selezionati in Europa garantiscono prodotti disponibili e spedizioni rapide. Ogni ordine è tracciabile con assistenza dedicata.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { icon: '📍', title: 'Magazzino Italia', desc: 'Spediamo da Italia e Europa', link: '/faq#spedizioni' },
              { icon: '🚚', title: 'Spedizione Rapida', desc: 'Consegna in 24-48h', link: '/faq#spedizioni' },
              { icon: '✅', title: 'Qualità Verificata', desc: 'Prodotti testati e garantiti', link: '/qualita' },
              { icon: '📞', title: 'Assistenza Dedicata', desc: 'Supporto in italiano', link: '/support' },
            ].map((item, i) => (
              <Link to={item.link} key={i}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-2xl p-6 text-center shadow-sm border border-neutral-100 hover:shadow-lg hover:border-[#1a5c3a]/20 transition-all cursor-pointer h-full"
                >
                  <span className="text-3xl mb-4 block">{item.icon}</span>
                  <h3 className="font-medium text-[#1a3d2a] mb-2">{item.title}</h3>
                  <p className="text-sm text-neutral-500 font-light">{item.desc}</p>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Opportunità Section - REFERENTE + VENDITORE */}
      <section className="py-16 md:py-20 bg-[#1a5c3a]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4 block">
              Due Modi per Guadagnare
            </span>
            <h2 className="text-3xl md:text-4xl font-light text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Lavora con <span className="italic">QANTRI</span>
            </h2>
            <p className="text-white/70 text-lg font-light max-w-2xl mx-auto">
              Scegli come vuoi guadagnare: condividi i nostri prodotti o apri il tuo negozio online
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* OPZIONE 1: REFERENTE */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-3xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="text-xl">🔗</span>
                </div>
                <div>
                  <h3 className="text-xl font-medium text-white">Diventa Referente</h3>
                  <p className="text-[#D4AF37] text-sm">Gratis • Senza rischi</p>
                </div>
              </div>
              
              <p className="text-white/80 font-light mb-6">
                Condividi i prodotti QANTRI con il tuo link personale e guadagna commissioni su ogni vendita. Perfetto per chi ha un seguito sui social.
              </p>
              
              <ul className="text-white/70 text-sm space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">✓</span>
                  <span>Guadagni <strong className="text-white">fino al 15%</strong> di commissione</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">✓</span>
                  <span>Nessun costo, inizi subito gratis</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">✓</span>
                  <span>Dashboard per monitorare i tuoi guadagni</span>
                </li>
              </ul>
              
              <Link to="/pricing">
                <Button className="w-full h-12 text-sm uppercase tracking-[0.1em] font-medium bg-[#D4AF37] text-[#1a3d2a] hover:bg-[#e5c349] rounded-full transition-all">
                  Diventa Referente
                </Button>
              </Link>
            </motion.div>
            
            {/* OPZIONE 2: VENDITORE */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border-2 border-[#D4AF37]/30"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                  <span className="text-xl">🏪</span>
                </div>
                <div>
                  <h3 className="text-xl font-medium text-white">Apri il Tuo Negozio</h3>
                  <p className="text-[#D4AF37] text-sm">Primo mese gratis</p>
                </div>
              </div>
              
              <p className="text-white/80 font-light mb-6">
                Hai prodotti tuoi da vendere? Crea il tuo negozio su QANTRI, carica i tuoi prodotti e inizia a vendere a migliaia di clienti.
              </p>
              
              <ul className="text-white/70 text-sm space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">✓</span>
                  <span>Carica e vendi i <strong className="text-white">tuoi prodotti</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">✓</span>
                  <span>Dashboard personale per gestire ordini e vendite</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">✓</span>
                  <span>Commissioni dal 10% al 20% (in base al piano)</span>
                </li>
              </ul>
              
              <Link to="/sell">
                <Button className="w-full h-12 text-sm uppercase tracking-[0.1em] font-medium bg-white text-[#1a3d2a] hover:bg-white/90 rounded-full transition-all">
                  Apri il Tuo Negozio
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-6 block">
              Inizia Oggi
            </span>
            <h2 className="text-4xl md:text-5xl font-light mb-6 tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              L&apos;eleganza ti <span className="italic">aspetta</span>
            </h2>
            <p className="text-neutral-500 font-light mb-10 max-w-xl mx-auto text-lg">
              Scopri la nostra selezione curata di oltre 2.000 prodotti premium.
            </p>
            <Link to="/products">
              <Button className="h-16 px-12 text-sm uppercase tracking-[0.2em] font-medium bg-[#064E3B] text-white hover:bg-[#043927] rounded-full transition-all duration-300 shadow-lg hover:shadow-xl">
                Esplora Ora
                <ArrowRight className="ml-3 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Chat Button - positioned above WhatsApp button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: "spring" }}
        onClick={() => setChatOpen(true)}
        className="fixed bottom-44 right-6 w-14 h-14 bg-[#064E3B] text-white rounded-full shadow-xl hover:bg-[#043927] transition-all duration-300 flex items-center justify-center z-50 group"
        data-testid="chat-button"
      >
        <MessageCircle className="h-6 w-6 group-hover:scale-110 transition-transform" />
      </motion.button>

      {/* Chat Modal */}
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed inset-x-4 bottom-4 md:inset-auto md:bottom-44 md:right-6 md:w-96 bg-white rounded-2xl border border-neutral-200 shadow-2xl z-[60] flex flex-col overflow-hidden"
            style={{ height: 'min(500px, calc(100vh - 120px))' }}
          >
            {/* Chat Header */}
            <div className="flex items-center justify-between p-5 border-b border-neutral-100 bg-[#064E3B] text-white">
              <div>
                <h3 className="text-sm font-medium">Assistenza QANTRI</h3>
                <p className="text-xs text-white/70">Siamo qui per aiutarti</p>
              </div>
              <button onClick={() => setChatOpen(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
            
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-neutral-50">
              {chatMessages.map((msg, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] p-4 text-sm rounded-2xl ${
                    msg.from === 'user' 
                      ? 'bg-[#064E3B] text-white rounded-br-none' 
                      : 'bg-white text-neutral-800 border border-neutral-100 rounded-bl-none shadow-sm'
                  }`}>
                    {msg.text}
                  </div>
                </motion.div>
              ))}
              <div ref={chatEndRef} />
            </div>
            
            {/* Chat Input */}
            <div className="p-4 border-t border-neutral-100 bg-white">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleChatSend()}
                  placeholder="Scrivi un messaggio..."
                  className="flex-1 px-4 py-3 border border-neutral-200 rounded-full text-sm focus:outline-none focus:border-[#064E3B] transition-colors"
                  data-testid="chat-input"
                />
                <button
                  onClick={handleChatSend}
                  className="w-12 h-12 bg-[#064E3B] text-white rounded-full hover:bg-[#043927] transition-colors flex items-center justify-center"
                  data-testid="chat-send"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hide scrollbar CSS */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default HomePage;
